/**
 * Settings → Navigation.
 *
 * The first cut of this screen laid the whole tree out in one column: four
 * groups, nineteen collections and their items, about fifty draggable rows.
 * Everything was reachable and nothing was arrangeable — a drag from the bottom
 * of Software Engg to the top of the page crossed two screenfuls, and the list
 * you were actually trying to order was never wholly in view.
 *
 * So the tree is split across two panes. The left holds the groups, which is a
 * short list by definition. The right holds one group at a time. Every list you
 * can drag within is now short enough to see whole, which is the property that
 * makes dragging work at all.
 *
 * Dragging is also not the only way: each row carries up/down buttons, and
 * Alt+↑/↓ moves a focused row. Precise ordering by pointer is fiddly at any
 * length, and a button press is exact.
 *
 * Everything is edited on a DRAFT. Cancel discards it; Save writes through the
 * same `useNavOrder` the sidebar reads, so there is one persistence path.
 * Nothing here can add, remove or re-parent anything — only order changes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Toast } from 'primereact/toast';
import { useNav } from '../../components/layout/use-nav';
import { orderFromNav } from '../../components/layout/use-nav-order';
import { resetNavOrder, saveNavOrder } from '../../features/navigation/navSlice';
import type { AppDispatch } from '../../store';
import SettingsLayout, { SettingsSection } from './settings-layout';
import type { NavGroup } from '../../components/layout/nav-model';

type Level = 'group' | 'collection' | 'item';

interface Held {
  level: Level;
  /** Group id for a collection, collection id for an item, '' for a group. */
  parent: string;
  index: number;
}

interface DropAt {
  level: Level;
  parent: string;
  index: number;
}

const move = <T,>(list: T[], from: number, to: number): T[] => {
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to > from ? to - 1 : to, 0, item);
  return next;
};

const at = (d: DropAt | null, level: Level, parent: string, index: number) =>
  !!d && d.level === level && d.parent === parent && d.index === index;

const NavigationSettings = () => {
  const toast = useRef<Toast>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { nav, userId, saving, error } = useNav();

  const [draft, setDraft] = useState<NavGroup[]>(nav);
  const [dirty, setDirty] = useState(false);
  /** Set on a successful save, cleared by the next edit. Drives the box below. */
  const [saved, setSaved] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [openCollection, setOpenCollection] = useState<string | null>(null);
  const held = useRef<Held | null>(null);
  const [heldKey, setHeldKey] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<DropAt | null>(null);

  useEffect(() => {
    if (!dirty) setDraft(nav);
  }, [nav, dirty]);

  // Open the first group so the right pane is never empty on arrival.
  useEffect(() => {
    if (!openGroup && draft.length) setOpenGroup(draft[0].id);
  }, [draft, openGroup]);

  const group = useMemo(
    () => draft.find((g) => g.id === openGroup) || draft[0],
    [draft, openGroup]
  );

  const applyMove = useCallback((h: Held, target: number) => {
    setDirty(true);
    setSaved(false);
    setDraft((prev) => {
      if (h.level === 'group') return move(prev, h.index, target);
      if (h.level === 'collection') {
        return prev.map((g) =>
          g.id === h.parent ? { ...g, collections: move(g.collections, h.index, target) } : g
        );
      }
      return prev.map((g) => ({
        ...g,
        collections: g.collections.map((c) =>
          c.id === h.parent ? { ...c, children: move(c.children, h.index, target) } : c
        ),
      }));
    });
  }, []);

  /** Up/down by one. Exact where a pointer is approximate. */
  const step = useCallback(
    (level: Level, parent: string, index: number, len: number, dir: -1 | 1) => {
      const to = index + dir;
      if (to < 0 || to >= len) return;
      applyMove({ level, parent, index }, dir < 0 ? to : to + 1);
    },
    [applyMove]
  );

  /* ── dragging ───────────────────────────────────────────────── */

  const onDragStart =
    (level: Level, parent: string, index: number, key: string) => (e: React.DragEvent) => {
      held.current = { level, parent, index };
      setHeldKey(key);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', key);
      const row = (e.currentTarget as HTMLElement).querySelector('[data-row]') as HTMLElement | null;
      if (row) e.dataTransfer.setDragImage(row, 16, row.offsetHeight / 2);
    };

  /**
   * Siblings only. A collection cannot be dropped into another group and an item
   * cannot leave its collection: the gesture is simply not accepted, so invalid
   * nesting is unreachable rather than caught and undone.
   */
  const onDragOver = (level: Level, parent: string, index: number) => (e: React.DragEvent) => {
    const h = held.current;
    if (!h || h.level !== level || h.parent !== parent) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const after = e.clientY - box.top > box.height / 2;
    setDropAt({ level, parent, index: index + (after ? 1 : 0) });
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const h = held.current;
    if (h && dropAt && h.level === dropAt.level && h.parent === dropAt.parent) {
      applyMove(h, dropAt.index);
    }
    endDrag();
  };

  const endDrag = () => {
    held.current = null;
    setHeldKey(null);
    setDropAt(null);
  };

  const onKeyDown =
    (level: Level, parent: string, index: number, len: number) => (e: React.KeyboardEvent) => {
      if (!e.altKey) return;
      const dir = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0;
      if (!dir) return;
      e.preventDefault();
      step(level, parent, index, len, dir as -1 | 1);
      const el = e.currentTarget as HTMLElement;
      requestAnimationFrame(() => el.focus());
    };

  /* ── pieces ─────────────────────────────────────────────────── */

  const Line = ({ on }: { on: boolean }) => (
    <div
      aria-hidden="true"
      className={`transition-all duration-150 ease-out ${on ? 'h-2 opacity-100' : 'h-0 opacity-0'}`}
    >
      <div className="h-0.5 rounded-full bg-indigo-500 dark:bg-indigo-400" />
    </div>
  );

  const Steps = ({
    level,
    parent,
    index,
    len,
    label,
  }: {
    level: Level;
    parent: string;
    index: number;
    len: number;
    label: string;
  }) => (
    <span className="ml-auto flex items-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
      <button
        type="button"
        disabled={index === 0}
        aria-label={`Move ${label} up`}
        onClick={() => step(level, parent, index, len, -1)}
        className="grid place-items-center w-6 h-6 rounded text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-200/70 dark:hover:bg-gray-600/50 disabled:opacity-25 disabled:hover:bg-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        <i className="pi pi-chevron-up text-[10px]" />
      </button>
      <button
        type="button"
        disabled={index === len - 1}
        aria-label={`Move ${label} down`}
        onClick={() => step(level, parent, index, len, 1)}
        className="grid place-items-center w-6 h-6 rounded text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-200/70 dark:hover:bg-gray-600/50 disabled:opacity-25 disabled:hover:bg-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        <i className="pi pi-chevron-down text-[10px]" />
      </button>
    </span>
  );

  const Handle = () => (
    <span
      aria-hidden="true"
      className="shrink-0 grid place-items-center w-5 h-6 rounded text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 cursor-grab active:cursor-grabbing transition-colors"
    >
      <i className="pi pi-bars text-[11px]" />
    </span>
  );

  const row =
    'group flex items-center gap-2 rounded-lg border border-transparent transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500';

  /**
   * Persist, and stay.
   *
   * Saving does not navigate away: the sidebar is right there, so the most
   * useful thing after a save is to see the new order in place and keep
   * adjusting it. Leaving for the profile made you come back to check.
   *
   * The sidebar is not told anything either. It selects the same slice this
   * thunk writes, so it has already re-rendered by the time the confirmation
   * appears. No reload, no refetch, no message passing.
   *
   * `saving` guards re-entry, so a double click cannot fire two saves.
   */
  const save = async () => {
    if (saving) return;
    const result = await dispatch(saveNavOrder({ userId, order: orderFromNav(draft) }));
    if (saveNavOrder.rejected.match(result)) {
      // The draft is deliberately left intact and `dirty` stays true, so the
      // arrangement is still on screen and Save can simply be pressed again.
      setSaved(false);
      toast.current?.show({
        severity: 'error',
        summary: 'Could not save',
        detail: (result.payload as string) || 'Your arrangement was not saved. Please try again.',
        life: 5000,
      });
      return;
    }
    setDirty(false);
    setSaved(true);
    toast.current?.show({
      severity: 'success',
      summary: 'Navigation saved',
      detail: 'Your sidebar now uses this arrangement.',
      life: 2500,
    });
  };

  /**
   * Discard and stay. On its own page Cancel meant "leave", but this is a
   * section of Settings now — there is nowhere it would sensibly go, and
   * throwing the draft away while leaving you where you are is what the button
   * actually means.
   */
  const cancel = () => {
    setDraft(nav);
    setDirty(false);
    setSaved(false);
  };

  const restore = () => {
    dispatch(resetNavOrder());
    setDirty(false);
    setSaved(false);
    toast.current?.show({ severity: 'info', summary: 'Default order restored', life: 2500 });
  };

  return (
    <SettingsLayout
      title="Navigation"
      description="Customize how your navigation is organized and displayed."
      testId="SCR-NavigationSettings"
    >
      <Toast ref={toast} position="top-right" />

      {/* The confirmation. An inline box rather than only a toast: a toast is
          gone in two seconds, and after a save you are still on this screen
          deciding whether the arrangement is right. It clears itself the moment
          you move something again, so it can never claim a stale state. */}
      {saved && (
        <div
          role="status"
          aria-live="polite"
          className="mb-3 flex items-start gap-2.5 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2.5"
          data-testid="CustomizeNav.Saved"
        >
          <i className="pi pi-check-circle text-emerald-600 dark:text-emerald-400 text-sm mt-px" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-emerald-900 dark:text-emerald-200">
              Navigation saved
            </p>
            <p className="text-[12.5px] text-emerald-800/80 dark:text-emerald-300/80">
              Your sidebar has been updated — the new order is already in place on the left.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSaved(false)}
            aria-label="Dismiss"
            className="shrink-0 grid place-items-center w-6 h-6 rounded text-emerald-700/70 dark:text-emerald-300/70 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            <i className="pi pi-times text-[11px]" />
          </button>
        </div>
      )}

      <SettingsSection
        title="Navigation order"
        hint="Arrange the order of your navigation groups and the items inside them. Nothing is added or removed — only the order changes."
      >
      <div className="rounded-lg border border-line overflow-hidden">
        <div className="grid md:grid-cols-[220px_1fr] divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-gray-700/60">
          {/* ── left: the groups ─────────────────────────────── */}
          <div className="p-3" onDragEnd={endDrag}>
            <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-muted">
              Sections
            </p>
            {draft.map((g, gi) => {
              const on = group?.id === g.id;
              return (
                <div key={g.id}>
                  <Line on={at(dropAt, 'group', '', gi)} />
                  <div
                    draggable
                    onDragStart={onDragStart('group', '', gi, `g:${g.id}`)}
                    onDragOver={onDragOver('group', '', gi)}
                    onDrop={onDrop}
                    className={`transition-opacity duration-150 ${
                      heldKey === `g:${g.id}` ? 'opacity-40' : 'opacity-100'
                    }`}
                  >
                    <div
                      data-row
                      tabIndex={0}
                      role="button"
                      aria-pressed={on}
                      aria-label={`${g.label}. Alt with arrow keys to reorder.`}
                      onClick={() => setOpenGroup(g.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenGroup(g.id);
                        }
                        onKeyDown('group', '', gi, draft.length)(e);
                      }}
                      className={`${row} w-full px-1.5 py-2 cursor-pointer ${
                        on
                          ? 'bg-indigo-50 dark:bg-indigo-400/10 border-indigo-100 dark:border-indigo-400/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                      }`}
                    >
                      <Handle />
                      <span
                        className={`min-w-0 truncate text-[13px] font-medium ${
                          on
                            ? 'text-indigo-700 dark:text-indigo-200'
                            : 'text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {g.label}
                      </span>
                      <Steps level="group" parent="" index={gi} len={draft.length} label={g.label} />
                    </div>
                  </div>
                  {gi === draft.length - 1 && <Line on={at(dropAt, 'group', '', gi + 1)} />}
                </div>
              );
            })}
          </div>

          {/* ── right: one group at a time ───────────────────── */}
          <div className="p-3 min-w-0" onDragEnd={endDrag}>
            <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-muted truncate">
              {group ? group.label : 'Nothing to arrange'}
            </p>

            {group?.collections.map((c, ci) => {
              const kids = c.children.length;
              const expanded = openCollection === c.id;
              return (
                <div key={c.id}>
                  <Line on={at(dropAt, 'collection', group.id, ci)} />
                  <div
                    draggable
                    onDragStart={onDragStart('collection', group.id, ci, `c:${c.id}`)}
                    onDragOver={onDragOver('collection', group.id, ci)}
                    onDrop={onDrop}
                    className={`transition-opacity duration-150 ${
                      heldKey === `c:${c.id}` ? 'opacity-40' : 'opacity-100'
                    }`}
                  >
                    <div
                      data-row
                      tabIndex={0}
                      role="button"
                      aria-label={`${c.label}, in ${group.label}. Alt with arrow keys to reorder.`}
                      onKeyDown={onKeyDown('collection', group.id, ci, group.collections.length)}
                      className={`${row} px-1.5 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 hover:border-gray-200 dark:hover:border-gray-600`}
                    >
                      <Handle />
                      <i
                        aria-hidden="true"
                        className={`${c.icon} text-sm w-4 text-center text-ink-muted shrink-0`}
                      />
                      <span className="min-w-0 truncate text-[13px] text-gray-700 dark:text-gray-200">
                        {c.label}
                      </span>

                      {/* Items stay folded away until asked for. Unfolding one
                          collection at a time is what keeps this pane short. */}
                      {kids > 0 && (
                        <button
                          type="button"
                          aria-expanded={expanded}
                          onClick={() => setOpenCollection(expanded ? null : c.id)}
                          className="shrink-0 ml-1 px-1.5 py-0.5 rounded text-[11px] text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-300 hover:bg-gray-200/60 dark:hover:bg-gray-600/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                        >
                          <i
                            className={`pi pi-chevron-right text-[9px] mr-1 inline-block transition-transform duration-200 ${
                              expanded ? 'rotate-90' : ''
                            }`}
                          />
                          Items
                        </button>
                      )}

                      <Steps
                        level="collection"
                        parent={group.id}
                        index={ci}
                        len={group.collections.length}
                        label={c.label}
                      />
                    </div>
                  </div>

                  {kids > 0 && (
                    <div
                      className="grid transition-all duration-200 ease-out"
                      style={{ gridTemplateRows: expanded ? '1fr' : '0fr', opacity: expanded ? 1 : 0 }}
                    >
                      <div className="overflow-hidden">
                        <div className="ml-5 pl-3 my-1 border-l border-gray-200 dark:border-gray-700/60">
                          {c.children.map((it, ii) => (
                            <div key={it.id}>
                              <Line on={at(dropAt, 'item', c.id, ii)} />
                              <div
                                draggable
                                onDragStart={onDragStart('item', c.id, ii, `i:${it.id}`)}
                                onDragOver={onDragOver('item', c.id, ii)}
                                onDrop={onDrop}
                                className={`transition-opacity duration-150 ${
                                  heldKey === `i:${it.id}` ? 'opacity-40' : 'opacity-100'
                                }`}
                              >
                                <div
                                  data-row
                                  tabIndex={0}
                                  role="button"
                                  aria-label={`${it.label}, in ${c.label}. Alt with arrow keys to reorder.`}
                                  onKeyDown={onKeyDown('item', c.id, ii, kids)}
                                  className={`${row} px-1.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40`}
                                >
                                  <Handle />
                                  <span className="min-w-0 truncate text-[12.5px] text-ink-muted">
                                    {it.label}
                                  </span>
                                  <Steps
                                    level="item"
                                    parent={c.id}
                                    index={ii}
                                    len={kids}
                                    label={it.label}
                                  />
                                </div>
                              </div>
                              {ii === kids - 1 && <Line on={at(dropAt, 'item', c.id, ii + 1)} />}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {ci === group.collections.length - 1 && (
                    <Line on={at(dropAt, 'collection', group.id, ci + 1)} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-800/40">
          <button
            type="button"
            onClick={restore}
            className="text-[13px] text-ink-muted hover:text-gray-800 dark:hover:text-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded px-1"
          >
            Restore default order
          </button>
          <div className="flex items-center gap-2">
            <span
              className={`text-[12px] mr-1 ${
                error ? 'text-rose-600 dark:text-rose-400' : 'text-gray-400'
              }`}
              role={error ? 'alert' : undefined}
            >
              {error ? 'Not saved' : dirty ? 'Unsaved changes' : saved ? 'All changes saved' : ''}
            </span>
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg text-[13px] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60 disabled:opacity-40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              aria-busy={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              {saving && <i className="pi pi-spin pi-spinner text-[11px]" aria-hidden="true" />}
              {saving ? 'Saving…' : error ? 'Try again' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
      </SettingsSection>
    </SettingsLayout>
  );
};

export default NavigationSettings;
